import React, { useEffect, useRef, useState } from "react";
import { api } from "../api/api";

const SOUND_PREF_KEY = "algodesk-sound-alerts";

// Plays a short two-note beep -- rising pitch for BUY, falling pitch for
// SELL -- using the Web Audio API directly so no external audio files need
// to be bundled/served. Wrapped in try/catch since some browsers block
// AudioContext until a user gesture has occurred on the page; a blocked
// beep should never break the monitor.
function playAlertBeep(side) {
  try {
    const Ctx = window.AudioContext || window.webkitAudioContext;
    if (!Ctx) return;
    const ctx = new Ctx();
    if (ctx.state === "suspended") ctx.resume();
    const now = ctx.currentTime;
    const freqs = side === "BUY" ? [660, 880] : [660, 440];
    freqs.forEach((freq, i) => {
      const start = now + i * 0.14;
      const osc = ctx.createOscillator();
      const gain = ctx.createGain();
      osc.type = "sine";
      osc.frequency.value = freq;
      gain.gain.setValueAtTime(0.0001, start);
      gain.gain.exponentialRampToValueAtTime(0.25, start + 0.02);
      gain.gain.exponentialRampToValueAtTime(0.0001, start + 0.12);
      osc.connect(gain).connect(ctx.destination);
      osc.start(start);
      osc.stop(start + 0.13);
    });
    setTimeout(() => ctx.close(), 500);
  } catch (e) {
    // Audio unavailable/blocked -- silently skip, never break the monitor.
  }
}

export default function StrategyMonitor({ strategy, onClose, onStopped }) {
  const isPaper = (strategy.config?.tradingMode || "Paper") === "Paper";
  const [status, setStatus] = useState({ state: "IDLE", legs: {}, realized_pnl: 0, unrealized_pnl: 0, running: false });
  const [logs, setLogs] = useState([]);
  const [dryRun, setDryRun] = useState(isPaper ? true : true);
  const [error, setError] = useState("");
  const [soundOn, setSoundOn] = useState(() => localStorage.getItem(SOUND_PREF_KEY) !== "0");
  const pollRef = useRef(null);
  const wasRunningRef = useRef(false);
  const soundOnRef = useRef(soundOn);
  // Tracks how many log rows we've already seen + whether the first fetch
  // for the current strategy has completed, so we alert only on genuinely
  // new buy/sell fills -- never replay a beep for a strategy's pre-existing
  // history when the monitor first mounts or you switch strategies.
  const seenLogCountRef = useRef(0);
  const initializedRef = useRef(false);

  useEffect(() => {
    soundOnRef.current = soundOn;
    localStorage.setItem(SOUND_PREF_KEY, soundOn ? "1" : "0");
  }, [soundOn]);

  const refresh = () => {
    api.strategyStatus(strategy.id).then((s) => {
      setStatus(s);
      // Catches the strategy stopping on its own (auto-exit time, max
      // profit/loss, an error) -- not just the user clicking Stop below --
      // so the strategy list's Run/Running button flips back within one
      // poll tick (3s) instead of waiting on the list's own slower refresh.
      if (wasRunningRef.current && !s.running && onStopped) onStopped();
      wasRunningRef.current = !!s.running;
    }).catch(() => {});
    api.strategyLogs(strategy.id).then((newLogs) => {
      // order_placed / order_placed_dryrun fire for every entry, exit, and
      // pyramid-add (see order_manager.py) and always carry a "side" of
      // BUY or SELL -- the single reliable hook for a buy/sell alert tone.
      if (initializedRef.current && soundOnRef.current && newLogs.length > seenLogCountRef.current) {
        newLogs.slice(seenLogCountRef.current).forEach((row) => {
          if (row.event !== "order_placed" && row.event !== "order_placed_dryrun") return;
          let side = null;
          try { side = JSON.parse(row.details_json).side; } catch (e) { /* ignore */ }
          if (side === "BUY" || side === "SELL") playAlertBeep(side);
        });
      }
      seenLogCountRef.current = newLogs.length;
      initializedRef.current = true;
      setLogs(newLogs);
    }).catch(() => {});
  };

  useEffect(() => {
    // Reset on every strategy switch -- without this, closing strategy A's
    // monitor (while it was running) and opening strategy B's in the same
    // slot would carry over A's "was running" flag and fire a spurious
    // onStopped() the instant B's first status poll comes back, and would
    // also replay alert beeps for B's pre-existing log history.
    wasRunningRef.current = false;
    seenLogCountRef.current = 0;
    initializedRef.current = false;
    refresh();
    pollRef.current = setInterval(refresh, 3000);
    return () => clearInterval(pollRef.current);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [strategy.id]);

  const start = async () => {
    setError("");
    try {
      await api.startStrategy(strategy.id, dryRun);
      refresh();
    } catch (err) {
      setError(err.message);
    }
  };

  const stop = async () => {
    await api.stopStrategy(strategy.id);
    refresh();
    if (onStopped) onStopped();
  };

  const totalPnl = (status.realized_pnl || 0) + (status.unrealized_pnl || 0);

  return (
    <div className="panel" style={{ margin: "0 20px 20px" }}>
      <div style={{ display: "flex", alignItems: "center", gap: 12, marginBottom: 12 }}>
        <div style={{ fontSize: 14, fontWeight: 600, flex: 1 }}>
          {strategy.alias_name || "(unnamed)"} — live monitor
        </div>
        <span style={stateBadge(status.state)}>{status.state}</span>
        <label style={{ fontSize: 11, color: "var(--text-dim)", display: "flex", alignItems: "center", gap: 5, cursor: "pointer" }}>
          <input
            type="checkbox"
            className="f-checkbox"
            checked={soundOn}
            onChange={(e) => setSoundOn(e.target.checked)}
          />
          Sound alerts
        </label>
        <button style={styles.closeBtn} onClick={onClose}>Close</button>
      </div>

      <div style={{ display: "flex", gap: 20, alignItems: "center", marginBottom: 14 }}>
        {!status.running ? (
          <>
            <label style={{ fontSize: 12, color: "var(--text-dim)", display: "flex", alignItems: "center", gap: 6 }}>
              <input
                type="checkbox"
                className="f-checkbox"
                checked={dryRun}
                disabled={isPaper}
                onChange={(e) => setDryRun(e.target.checked)}
              />
              Dry run (simulated fills, no real orders)
            </label>
            {isPaper && (
              <span style={{ fontSize: 11, color: "var(--text-dim)" }}>
                This strategy is set to Paper trading — edit it and switch Trading Mode to Real to allow live orders.
              </span>
            )}
            <button className="btn btn-primary" onClick={start}>Start Strategy</button>
          </>
        ) : (
          <button className="btn btn-secondary" style={{ borderColor: "var(--danger)", color: "var(--danger)" }} onClick={stop}>
            Stop Strategy
          </button>
        )}
        {!dryRun && !status.running && (
          <span style={{ fontSize: 11, color: "var(--warn)" }}>Live mode — this will place real orders.</span>
        )}
        {error && <span style={{ fontSize: 12, color: "var(--danger)" }}>{error}</span>}
      </div>

      <div style={{ display: "grid", gridTemplateColumns: "repeat(3, 1fr)", gap: 12, marginBottom: 14 }}>
        <Stat label="Realized P&L" value={status.realized_pnl} />
        <Stat label="Unrealized P&L" value={status.unrealized_pnl} />
        <Stat label="Total P&L" value={totalPnl} emphasize />
      </div>

      {Object.keys(status.legs || {}).length > 0 && (
        <table style={styles.table}>
          <thead>
            <tr>
              <th style={styles.th}>Leg</th>
              <th style={styles.th}>Side</th>
              <th style={styles.th}>Qty</th>
              <th style={styles.th}>Entry</th>
              <th style={styles.th}>Trail Stop (pts)</th>
            </tr>
          </thead>
          <tbody>
            {Object.entries(status.legs).map(([key, leg]) => (
              <React.Fragment key={key}>
                <tr>
                  <td style={styles.td}>{key}</td>
                  <td style={styles.td}>{leg.side}</td>
                  <td style={styles.td}>{leg.qty}</td>
                  <td style={styles.td}>{leg.entry_price?.toFixed ? leg.entry_price.toFixed(2) : leg.entry_price}</td>
                  <td style={styles.td}>{leg.trail_stop ?? "—"}</td>
                </tr>
                {/* Per-lot breakdown -- only worth showing once pyramiding
                    has actually added a lot; a single-fill leg's "Entry"
                    column above already says everything there is to say. */}
                {leg.fills && leg.fills.length > 1 && (
                  <tr>
                    <td colSpan={5} style={{ ...styles.td, background: "var(--panel-alt)", fontSize: 11 }}>
                      {leg.fills.map((f, i) => (
                        <span key={i} style={{ marginRight: 14, color: "var(--text-dim)" }}>
                          Lot {i + 1}: {f.qty}@{Number(f.price).toFixed(2)}
                          {f.rung ? ` (rung ${f.rung})` : " (initial)"}
                        </span>
                      ))}
                    </td>
                  </tr>
                )}
              </React.Fragment>
            ))}
          </tbody>
        </table>
      )}

      <div style={{ marginTop: 14 }}>
        <div className="panel-title">Event Log</div>
        <div style={styles.logBox}>
          {logs.length === 0 && <div style={{ color: "var(--text-dim)" }}>No events yet.</div>}
          {logs.slice().reverse().map((row, i) => (
            <div key={i} style={styles.logRow}>
              <span style={{ color: "var(--text-dim)" }}>{new Date(row.timestamp).toLocaleTimeString()}</span>{" "}
              <span style={{ color: "var(--focus)" }}>{row.event}</span>{" "}
              <span>{row.details_json}</span>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

function Stat({ label, value, emphasize }) {
  const v = value || 0;
  const color = v > 0 ? "var(--accent)" : v < 0 ? "var(--danger)" : "var(--text-dim)";
  return (
    <div style={{ background: "var(--panel-alt)", border: "1px solid var(--border)", borderRadius: 6, padding: "10px 12px" }}>
      <div style={{ fontSize: 10, color: "var(--text-dim)", textTransform: "uppercase" }}>{label}</div>
      <div style={{ fontFamily: "var(--font-mono)", fontSize: emphasize ? 18 : 15, fontWeight: 600, color }}>
        {v.toFixed(2)}
      </div>
    </div>
  );
}

function stateBadge(state) {
  const colors = {
    RUNNING: "var(--accent)", MANAGING_POSITION: "var(--accent)",
    ERROR: "var(--danger)", STOPPED: "var(--text-dim)", IDLE: "var(--text-dim)",
    // Sleeping outside market hours -- still "running" (thread alive,
    // will auto-resume next session) but visually distinct from actively
    // watching for entries/managing a position.
    HIBERNATING: "var(--warn)",
  };
  return {
    fontSize: 10, padding: "3px 8px", borderRadius: 4, textTransform: "uppercase",
    background: "var(--panel-alt)", border: `1px solid ${colors[state] || "var(--border)"}`,
    color: colors[state] || "var(--text-dim)",
  };
}

const styles = {
  closeBtn: { background: "none", border: "1px solid var(--border)", color: "var(--text-dim)", borderRadius: 5, padding: "5px 10px", fontSize: 11, cursor: "pointer" },
  table: { width: "100%", borderCollapse: "collapse", fontSize: 12 },
  th: { textAlign: "left", color: "var(--text-dim)", fontWeight: 500, padding: "6px 8px", borderBottom: "1px solid var(--border)" },
  td: { padding: "7px 8px", borderBottom: "1px solid var(--border)", fontFamily: "var(--font-mono)" },
  logBox: { maxHeight: 220, overflowY: "auto", background: "var(--panel-alt)", border: "1px solid var(--border)", borderRadius: 6, padding: "8px 10px", fontSize: 11.5, fontFamily: "var(--font-mono)", display: "flex", flexDirection: "column", gap: 4 },
  logRow: { whiteSpace: "pre-wrap", wordBreak: "break-word" },
};
